// @ts-nocheck

import React, { useRef, useState, useMemo, useEffect } from 'react';
import { AutoSizer, WindowScroller } from 'react-virtualized';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { getAppDOMElement } from '../../DOMElements';
import useWinabetLibContext from '../../winabet-lib/hooks/useWinabetLibContext';

const Wrapper = styled.div`
  ${({ $withLeftShadow, $withRightShadow }) => css`
    transform-style: preserve-3d;

    ${Target.__MOBILE__
      ? css`
          &:before {
            content: '';
            position: absolute;
            width: 3em;
            height: 100%;
            top: 0;
            left: -0.1em;
            transform: translateZ(2px);

            background: linear-gradient(
              to right,
              var(--ScrollViewLeftShadow-backgroundColor, transparent),
              transparent
            );
            transition: opacity 200ms ease-in-out;
            pointer-events: none;
          }

          &:after {
            content: '';
            position: absolute;
            width: 3em;
            height: 100%;
            top: 0;
            right: -0.1em;
            transform: translateZ(2px);

            background: linear-gradient(
              to left,
              var(--ScrollViewRightShadow-backgroundColor, transparent),
              transparent
            );
            transition: opacity 200ms ease-in-out;
            pointer-events: none;
          }

          ${!$withLeftShadow
            ? css`
                &:before {
                  opacity: 0;
                }
              `
            : undefined}
          ${!$withRightShadow
            ? css`
                &:after {
                  opacity: 0;
                }
              `
            : undefined}
        `
      : undefined}
  `}
`;

const OverflowScrollWrapper = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;

  ::-webkit-scrollbar {
    display: none;
  }

  ${({ $vertical, $contentSpacing }) => css`
    ${$vertical ? 'overflow-y: scroll;' : 'overflow-x: scroll;'}
    padding: ${$contentSpacing ? `${$contentSpacing}px` : undefined};
  `}
`;

const ScrollView = ({
  vertical = true,
  children,
  style,
  className,
  contentSpacing,
  disableAutoSizerHeight = false,
}) => {
  const scrollNodeRef = useRef();
  const overflowWrapperNodeRef = useRef();
  const scrollEventListenerRef = useRef();
  const wheelEventListenerRef = useRef();

  const [isScrollPositionAtStart, setIsScrollPositionAtStart] = useState(true);
  const [isScrollPositionAtEnd, setIsScrollPositionAtEnd] = useState(false);

  const winabetLibContext = useWinabetLibContext();
  const scrollElement = useMemo(() => {
    if (Target.__LIBRARY__) {
      return winabetLibContext?.parentRef.current;
    }

    if (Target.__MOBILE__) {
      return getAppDOMElement();
    }

    return window;
  }, [winabetLibContext]);

  useEffect(() => {
    if (vertical) {
      return;
    }

    if (Target.__MOBILE__) {
      const scrollEventListener = scrollEventListenerRef.current;

      const innerNode = overflowWrapperNodeRef.current?.childNodes[0];
      if (innerNode && scrollEventListener) {
        overflowWrapperNodeRef.current.removeEventListener('scroll', scrollEventListener);
      }

      if (innerNode) {
        const innerNodeWidth = innerNode.getBoundingClientRect()?.width;
        const wrapperNodeWidth = overflowWrapperNodeRef.current.getBoundingClientRect()?.width;

        if (innerNodeWidth && wrapperNodeWidth) {
          const scrollEventListener = (e) => {
            const { scrollLeft } = e.target;
            setIsScrollPositionAtStart(scrollLeft <= 10);
            setIsScrollPositionAtEnd(scrollLeft >= innerNodeWidth - wrapperNodeWidth - 10);
          };
          scrollEventListenerRef.current = scrollEventListener;
          overflowWrapperNodeRef.current.addEventListener('scroll', scrollEventListenerRef.current);
        }
      }
    } else {
      // Support mouse wheel horizontal on desktop
      const wheelEventListener = wheelEventListenerRef.current;
      const innerNode = overflowWrapperNodeRef.current?.childNodes[0];
      if (innerNode) {
        overflowWrapperNodeRef.current.removeEventListener('wheel', wheelEventListener);
      }

      if (innerNode) {
        const innerNodeWidth = innerNode.getBoundingClientRect()?.width;
        const wrapperNodeWidth = overflowWrapperNodeRef.current.getBoundingClientRect()?.width;

        if (innerNodeWidth && wrapperNodeWidth) {
          const wheelEventListener = (e) => {
            overflowWrapperNodeRef.current.scrollLeft += e.deltaY;
            e.preventDefault();
          };
          wheelEventListenerRef.current = wheelEventListener;
          overflowWrapperNodeRef.current.addEventListener('wheel', wheelEventListenerRef.current);
        }
      }
    }
  }, [children, vertical]);

  return (
    <Wrapper
      style={style}
      className={className}
      ref={scrollNodeRef}
      $withLeftShadow={!vertical && !isScrollPositionAtStart}
      $withRightShadow={!vertical && !isScrollPositionAtEnd}
    >
      {vertical ? (
        <WindowScroller scrollElement={scrollElement}>
          {() => (
            <AutoSizer disableHeight={disableAutoSizerHeight}>
              {({ width, height }) => (
                <OverflowScrollWrapper $vertical style={{ width, height }} $contentSpacing={contentSpacing}>
                  {children}
                </OverflowScrollWrapper>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      ) : (
        <OverflowScrollWrapper ref={overflowWrapperNodeRef}>{children}</OverflowScrollWrapper>
      )}
    </Wrapper>
  );
};

export default ScrollView;
